import type {
  InfiniteData,
  QueryKey,
  UseMutationResult,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import {
  ClientError,
  makeApiProvider,
  useApiQuery,
  usePhoenixApiInfiniteQuery,
  useSuspenseApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  CancelReceiptBatchEntryResult,
  GetReceiptBatchListParams,
  GetReceiptBatchListResult,
  IReceiptBatchPhoenixApi,
  ReceiptBatch,
  ReceiptBatchResult,
  UpdateReceiptBatch,
  UpdateReceiptBatchEntry,
} from './api';
import { ReceiptBatchPhoenixApi } from './api';
import {
  CANCEL_RECEIPT_BATCH_ENTRY_KEY,
  DELETE_RECEIPT_BATCH_ENTRY_KEY,
  DELETE_RECEIPT_BATCH_KEY,
  PATCH_RECEIPT_BATCH_KEY,
  POST_RECEIPT_BATCH_KEY,
  RETRACT_RECEIPT_BATCH_KEY,
  SUBMIT_RECEIPT_BATCH_KEY,
} from '../receipt-batch';
import { getReceiptBatchPaymentPhoenix } from '../payment-phoenix';
import {
  RECEIPT_BATCH_LIST_PHOENIX_KEY,
  RECEIPT_BATCH_PHOENIX_KEY,
} from './queryKeys';
import type {
  Gender,
  GetReceiptBatchResultDto,
  PaginatedReceiptBatchEntryDto,
  ReceiptBatchState,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { isDefined } from '@pflegenavi/shared/utils';
import { isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';

const ApiContext = createContext<IReceiptBatchPhoenixApi | undefined>(
  undefined
);
const {
  useApi: useReceiptBatchPhoenixApi,
  ApiProvider: ReceiptBatchPhoenixApiProvider,
} = makeApiProvider({
  name: 'ReceiptBatchPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new ReceiptBatchPhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useReceiptBatchPhoenixApi,
  ReceiptBatchPhoenixApiProvider,
  ApiContext as ReceiptBatchPhoenixApiContext,
  UpdateReceiptBatch,
  ReceiptBatch,
  CancelReceiptBatchEntryResult,
};
export const useGetReceiptBatchListPhoenix = (
  params: GetReceiptBatchListParams,
  options?: Omit<
    UseSuspenseQueryOptions<GetReceiptBatchListResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseSuspenseQueryResult<GetReceiptBatchListResult, unknown> => {
  const select = useCallback(
    (data: GetReceiptBatchListResult): GetReceiptBatchListResult => {
      return {
        ...data,
        data: data.data.map((row) => ({
          ...row,
          receipt_date: row.receipt_date
            ? new Date(row.receipt_date)
            : undefined,
          min_receipt_date: row.min_receipt_date
            ? new Date(row.min_receipt_date)
            : undefined,
          max_receipt_date: row.max_receipt_date
            ? new Date(row.max_receipt_date)
            : undefined,
          created_on: new Date(row.created_on),
          updated_on: new Date(row.updated_on),
          accepted_on: row.accepted_on ? new Date(row.accepted_on) : undefined,
          receipt_batch_entries: row.receipt_batch_entries.map((entry) => ({
            ...entry,
            receipt_date: entry.receipt_date
              ? new Date(entry.receipt_date)
              : undefined,
          })),
        })),
      };
    },
    []
  );
  return useSuspenseApiQuery(
    useReceiptBatchPhoenixApi,
    RECEIPT_BATCH_LIST_PHOENIX_KEY(params),
    (api) => {
      return api.getReceiptBatchList({
        params,
      });
    },
    {
      ...options,
      select,
    }
  );
};

export const useGetReceiptBatchListPhoenixNoSuspense = (
  params: GetReceiptBatchListParams,
  options?: Omit<
    UseQueryOptions<GetReceiptBatchListResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetReceiptBatchListResult, unknown> => {
  const select = useCallback(
    (data: GetReceiptBatchListResult): GetReceiptBatchListResult => {
      return {
        ...data,
        data: data.data.map((row) => ({
          ...row,
          receipt_date: row.receipt_date
            ? new Date(row.receipt_date)
            : undefined,
          min_receipt_date: row.min_receipt_date
            ? new Date(row.min_receipt_date)
            : undefined,
          max_receipt_date: row.max_receipt_date
            ? new Date(row.max_receipt_date)
            : undefined,
          created_on: new Date(row.created_on),
          updated_on: new Date(row.updated_on),
          accepted_on: row.accepted_on ? new Date(row.accepted_on) : undefined,
          receipt_batch_entries: row.receipt_batch_entries.map((entry) => ({
            ...entry,
            receipt_date: entry.receipt_date
              ? new Date(entry.receipt_date)
              : undefined,
          })),
        })),
      };
    },
    []
  );
  return useApiQuery(
    useReceiptBatchPhoenixApi,
    RECEIPT_BATCH_LIST_PHOENIX_KEY(params),
    (api) => {
      return api.getReceiptBatchList({
        params,
      });
    },
    {
      ...options,
      select,
    }
  );
};

export const useGetReceiptBatchListPhoenixInfiniteQuery = (
  params: Omit<GetReceiptBatchListParams, 'page'>,
  options?: Omit<
    UseInfiniteQueryOptions<
      GetReceiptBatchListResult,
      unknown,
      InfiniteData<GetReceiptBatchListResult>,
      GetReceiptBatchListResult,
      QueryKey,
      number
    >,
    | 'queryKey'
    | 'queryFn'
    | 'initialPageParam'
    | 'getNextPageParam'
    | 'getPreviousPageParam'
  >
): UseInfiniteQueryResult<InfiniteData<GetReceiptBatchListResult>, unknown> => {
  const select = useCallback(
    (
      data: InfiniteData<GetReceiptBatchListResult>
    ): InfiniteData<GetReceiptBatchListResult> => {
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: page.data.map((row) => ({
            ...row,
            receipt_date: row.receipt_date
              ? new Date(row.receipt_date)
              : undefined,
            min_receipt_date: row.min_receipt_date
              ? new Date(row.min_receipt_date)
              : undefined,
            max_receipt_date: row.max_receipt_date
              ? new Date(row.max_receipt_date)
              : undefined,
            created_on: new Date(row.created_on),
            updated_on: new Date(row.updated_on),
            accepted_on: row.accepted_on
              ? new Date(row.accepted_on)
              : undefined,
            receipt_batch_entries: row.receipt_batch_entries.map((entry) => ({
              ...entry,
              receipt_date: entry.receipt_date
                ? new Date(entry.receipt_date)
                : undefined,
            })),
          })),
        })),
      };
    },
    []
  );

  return usePhoenixApiInfiniteQuery<
    IReceiptBatchPhoenixApi,
    InfiniteData<GetReceiptBatchListResult>,
    GetReceiptBatchListResult
  >(
    useReceiptBatchPhoenixApi,
    RECEIPT_BATCH_LIST_PHOENIX_KEY({ ...params, page: 1 }),
    (api, page) => {
      return api.getReceiptBatchList({
        params: {
          ...params,
          page,
        },
      });
    },
    {
      ...options,
      select,
    }
  );
};

export const transformPhoenixReceiptBatchToLegacyReceiptBatch = (
  row: ReceiptBatch
): GetReceiptBatchResultDto & { receiptImagesRequired: boolean } => {
  return {
    id: row.id,
    acceptedDate: row.accepted_on,
    createdOn: row.created_on,
    generateReceipts: row.generate_receipts,
    nursingHomeId: row.nursing_home.id,
    nursingHomeName: row.nursing_home.name,
    receiptImagesRequired: row.nursing_home.receipt_images_required,
    receiptDate: row.receipt_date!,
    state: row.state as ReceiptBatchState,
    tenant: row.nursing_home.tenant,
    receiptImageIds: row.receipt_image_ids,
    updatedOn: row.updated_on,
    title: 'Deprecated',
    useIndividualDates: row.use_individual_dates,
    receiptBatchEntries: row.receipt_batch_entries.map((entry) => ({
      id: entry.id,
      residentId: entry.resident.id,
      receiptImageIds: entry.receipt_images.map((image) => image.id),
      receiptDate: entry.receipt_date,
      resident: {
        id: entry.resident.id,
        gender: entry.resident.gender as Gender,
        firstName: entry.resident.firstname,
        lastName: entry.resident.lastname,
      },
      lineItems: entry.line_items.map((lineItem) => ({
        id: lineItem.id,
        amountPerItemInCent: lineItem.amount_per_item,
        name: lineItem.name,
        count: 1,
        taxes: lineItem.taxes.map((tax) => ({
          countryId: tax.country_id,
          displayExplanation: tax.display_explanation,
          taxAmount: parseFloat(tax.tax_amount),
          taxName: tax.tax_name,
          explanationLong: tax.explanation_long,
          explanationShort: tax.explanation_short,
          taxAbbreviation: tax.tax_abbreviation,
          paragraph: tax.paragraph,
        })),
      })),
      payout_reconciliations: entry.payout_reconciliations,
    })),
  };
};

export const transformPhoenixListToLegacyList = (
  data: GetReceiptBatchListResult
): {
  data: PaginatedReceiptBatchEntryDto[];
  meta: {
    totalItems: number;
  };
} => {
  return {
    data: data.data.map((row) => {
      const receiptDates = (row?.receipt_batch_entries ?? [])
        .map((entry) => {
          return entry.receipt_date ? entry.receipt_date.getTime() : undefined;
        })
        .filter(isDefined);

      const minReceiptDateTime = new Date(Math.min(...receiptDates));
      const maxReceiptDateTime = new Date(Math.max(...receiptDates));

      const minReceiptDate = isValid(minReceiptDateTime)
        ? new Date(minReceiptDateTime)
        : undefined;
      const maxReceiptDate = isValid(maxReceiptDateTime)
        ? new Date(maxReceiptDateTime)
        : undefined;

      return {
        id: row.id,
        acceptedDate: row.accepted_on,
        createdOn: row.created_on,
        entryCount: row.entry_count,
        generateReceipts: row.generate_receipts,
        invalidEntryCount: row.invalid_entry_count,
        paidOutEntryCount: row.paid_out_entry_count,
        nursingHomeId: row.nursing_home.id,
        nursingHomeName: row.nursing_home.name,
        payment: row.payment
          ? {
              id: row.payment.id,
              status: row.payment.status,
              amountPaid: row.payment.amount_paid,
              cancelledAmount: row.payment.cancelled_amount ?? null,
            }
          : undefined,
        receiptDate: row.receipt_date,
        minReceiptDate: minReceiptDate,
        maxReceiptDate: maxReceiptDate,
        state: row.state as ReceiptBatchState,
        tenant: row.nursing_home.tenant,
        receiptImageIds: row.receipt_image_ids,
        updatedOn: row.updated_on,
        title: 'Deprecated',
        totalAmount: row.total_amount,
        useIndividualDates: row.use_individual_dates,
        entries: row.receipt_batch_entries,
      };
    }),
    meta: {
      totalItems: data.meta.total_count,
    },
  };
};

function selectGetReceiptBatch({ data }: ReceiptBatchResult): ReceiptBatch {
  return {
    ...data,
    receipt_date: data.receipt_date ? new Date(data.receipt_date) : undefined,
    created_on: new Date(data.created_on),
    updated_on: new Date(data.updated_on),
    accepted_on: data.accepted_on ? new Date(data.accepted_on) : undefined,
    receipt_batch_entries: data.receipt_batch_entries.map((entry) => ({
      ...entry,
      receipt_date: entry.receipt_date
        ? new Date(entry.receipt_date)
        : undefined,
    })),
  };
}

export const useGetReceiptBatchCallbackPhoenix = (
  receiptBatchId: string
): (() => Promise<ReceiptBatch>) => {
  const api = useReceiptBatchPhoenixApi();
  return useCallback(async () => {
    const result = await api.getReceiptBatch({
      params: { batchId: receiptBatchId },
    });
    return selectGetReceiptBatch(result);
  }, [api, receiptBatchId]);
};
export const useGetReceiptBatchPhoenix = (
  receiptBatchId: string | undefined,
  options?: Omit<
    UseQueryOptions<
      ReceiptBatchResult,
      unknown,
      GetReceiptBatchResultDto & { receiptImagesRequired: boolean }
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  GetReceiptBatchResultDto & { receiptImagesRequired: boolean },
  unknown
> => {
  const select = useCallback((data: ReceiptBatchResult) => {
    const part1 = selectGetReceiptBatch(data);
    return transformPhoenixReceiptBatchToLegacyReceiptBatch(part1);
  }, []);

  return useApiQuery(
    useReceiptBatchPhoenixApi,
    RECEIPT_BATCH_PHOENIX_KEY(receiptBatchId),
    (api) => {
      if (receiptBatchId === undefined) {
        throw new Error('receiptBatchId is undefined');
      }

      return api.getReceiptBatch({
        params: {
          batchId: receiptBatchId,
        },
      });
    },
    {
      select,
      enabled: receiptBatchId !== undefined,
      ...options,
    }
  );
};

export const useGetReceiptBatchPhoenixCallback = (
  receiptBatchId: string
): (() => Promise<GetReceiptBatchResultDto>) => {
  const api = useReceiptBatchPhoenixApi();
  const select = useCallback((data: ReceiptBatchResult) => {
    const part1 = selectGetReceiptBatch(data);
    return transformPhoenixReceiptBatchToLegacyReceiptBatch(part1);
  }, []);
  return useCallback(async () => {
    const result = await api.getReceiptBatch({
      params: { batchId: receiptBatchId },
    });
    return select(result);
  }, [api, receiptBatchId, select]);
};

export const usePostReceiptBatchPhoenix = (): UseMutationResult<
  ReceiptBatch,
  unknown,
  UpdateReceiptBatch
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<ReceiptBatch, unknown, UpdateReceiptBatch>({
    mutationKey: POST_RECEIPT_BATCH_KEY,
    mutationFn: (data) =>
      api
        .postReceiptBatch({
          body: data,
        })
        .then((res) => selectGetReceiptBatch(res)),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
      });
    },
  });
  return result;
};

export const usePatchReceiptBatchPhoenix = (): UseMutationResult<
  ReceiptBatch,
  unknown,
  UpdateReceiptBatch & {
    id: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  const result = useMutation<
    ReceiptBatch,
    unknown,
    UpdateReceiptBatch & {
      id: string;
    }
  >({
    mutationKey: PATCH_RECEIPT_BATCH_KEY,
    mutationFn: (data) =>
      api
        .patchReceiptBatch({
          params: { batchId: data.id },
          body: data,
        })
        .then(selectGetReceiptBatch),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const usePostReceiptBatchEntryPhoenix = (): UseMutationResult<
  ReceiptBatchResult,
  unknown,
  {
    batchId: string;
    data: UpdateReceiptBatchEntry[];
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  const result = useMutation<
    ReceiptBatchResult,
    unknown,
    {
      batchId: string;
      data: UpdateReceiptBatchEntry[];
    }
  >({
    mutationKey: ['POST_RECEIPT_BATCH_ENTRY_KEY'], // Changed to array
    mutationFn: (data) =>
      api.postReceiptBatchEntry({
        params: { batchId: data.batchId },
        body: { data: data.data },
      }),
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const usePatchReceiptBatchEntryPhoenix = (): UseMutationResult<
  ReceiptBatchResult,
  unknown,
  UpdateReceiptBatchEntry & {
    batchId: string;
    entryId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  const result = useMutation<
    ReceiptBatchResult,
    unknown,
    UpdateReceiptBatchEntry & {
      batchId: string;
      entryId: string;
    }
  >({
    mutationKey: ['PATCH_RECEIPT_BATCH_ENTRY_KEY'],
    mutationFn: (data) =>
      api.patchReceiptBatchEntry({
        params: {
          batchId: data.batchId,
          entryId: data.entryId,
        },
        body: data,
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const useDeleteReceiptBatchPhoenix = (): UseMutationResult<
  undefined,
  unknown,
  {
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  return useMutation<
    undefined,
    unknown,
    {
      batchId: string;
    }
  >({
    mutationKey: [DELETE_RECEIPT_BATCH_KEY], // Changed to array
    mutationFn: (data) =>
      api.deleteReceiptBatch({
        params: { batchId: data.batchId },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
      });
    },
  });
};

export const useDeleteReceiptBatchEntryPhoenix = (): UseMutationResult<
  ReceiptBatchResult,
  unknown,
  {
    batchId: string;
    entryId: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    ReceiptBatchResult,
    unknown,
    {
      batchId: string;
      entryId: string;
    }
  >({
    mutationKey: DELETE_RECEIPT_BATCH_ENTRY_KEY,
    mutationFn: (data) =>
      api.deleteReceiptBatchEntry({
        params: {
          batchId: data.batchId,
          entryId: data.entryId,
        },
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const useDeleteReceiptBatchEntriesPhoenix = (): UseMutationResult<
  ReceiptBatchResult,
  unknown,
  {
    batchId: string;
    entryIds: string[];
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    ReceiptBatchResult,
    unknown,
    {
      batchId: string;
      entryIds: string[];
    }
  >({
    mutationKey: ['DELETE_RECEIPT_BATCH_ENTRIES'],
    mutationFn: (data) =>
      api.deleteReceiptBatchEntries({
        params: {
          batchId: data.batchId,
        },
        body: {
          entry_ids: data.entryIds,
        },
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const useSubmitReceiptBatchPhoenix = (): UseMutationResult<
  {
    status: 'OK';
  },
  unknown,
  {
    batchId: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    {
      status: 'OK';
    },
    unknown,
    {
      batchId: string;
    }
  >({
    mutationKey: SUBMIT_RECEIPT_BATCH_KEY,
    mutationFn: (data) =>
      api.submitReceiptBatch({
        params: { batchId: data.batchId },
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const useRetractReceiptBatchPhoenix = (): UseMutationResult<
  {
    status: 'OK';
  },
  unknown,
  {
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  const result = useMutation<
    {
      status: 'OK';
    },
    unknown,
    {
      batchId: string;
    }
  >({
    mutationKey: RETRACT_RECEIPT_BATCH_KEY,
    mutationFn: (data) =>
      api.retractReceiptBatch({
        params: { batchId: data.batchId },
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

const useHandleError = (
  errorSnackbar: (error: Error, message: string) => void
) => {
  const { t } = useTranslation();
  return useCallback(
    (e: unknown) => {
      if (e instanceof ClientError && 'errors' in e) {
        // @ts-expect-error costCenter untyped
        if (e.errors?.costCenter?.includes("can't be blank")) {
          errorSnackbar(
            e,
            t(`receipt-batch.preview.error-missing-cost-center`)
          );
        }
        // @ts-expect-error serviceProvider untyped
        else if (e.errors?.serviceProvider?.includes("can't be blank")) {
          errorSnackbar(
            e,
            t(`receipt-batch.preview.error-missing-service-provider`)
          );
        }
        return;
      }

      errorSnackbar(e as Error, t('errors.something-went-wrong'));
    },
    [errorSnackbar, t]
  );
};

export const useGenerateReceiptBatchPreview = (
  errorSnackbar: (error: Error, message: string) => void
): UseMutationResult<
  Blob,
  unknown,
  {
    batchId: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();

  const handleError = useHandleError(errorSnackbar);

  const result = useMutation<
    Blob,
    unknown,
    {
      batchId: string;
    }
  >({
    mutationKey: ['GENERATE_RECEIPT_BATCH_REVIEW'],
    mutationFn: (data) =>
      api.postReceiptBatchPreview({
        params: { batchId: data.batchId },
      }),
    onError: handleError,
  });
  return result;
};

export const useGenerateReceiptBatchEntryPreview = (
  errorSnackbar: (error: Error, message: string) => void
): UseMutationResult<
  Blob,
  unknown,
  {
    batchId: string;
    entryId: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const handleError = useHandleError(errorSnackbar);
  const result = useMutation<
    Blob,
    unknown,
    {
      batchId: string;
      entryId: string;
    }
  >({
    mutationKey: ['GENERATE_RECEIPT_BATCH_ENTRY_REVIEW'],
    mutationFn: (data) => {
      return api.postReceiptBatchEntryPreview({
        params: {
          batchId: data.batchId,
          entryId: data.entryId,
        },
      });
    },
    onError: handleError,
  });
  return result;
};

export const useCancelReceiptBatchEntryPhoenix = (): UseMutationResult<
  CancelReceiptBatchEntryResult,
  unknown,
  {
    batchId: string;
    entryId: string;
    note: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    CancelReceiptBatchEntryResult,
    unknown,
    {
      batchId: string;
      entryId: string;
      note: string;
    }
  >({
    mutationKey: CANCEL_RECEIPT_BATCH_ENTRY_KEY,
    mutationFn: (data) =>
      api.cancelReceiptBatchEntry({
        params: { batchId: data.batchId, entryId: data.entryId },
        body: { data: { note: data.note } },
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: getReceiptBatchPaymentPhoenix(variables.batchId),
        }),
      ]);
    },
  });
};
